import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { DataService } from "src/app/Services/dataservice.service";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { EventEmitterService } from "src/app/Services/event-emitter.service";
import { UserserviceService } from "src/app/Services/userservice.service";
import { ThemePreferenceService, ThemeMode } from "src/app/Services/theme-preference.service";
import { Subscription } from "rxjs";

interface FAQItem {
  question: string;
  answer: string;
  isOpen: boolean;
}

interface FAQCategory {
  name: string;
  faqs: FAQItem[];
}

@Component({
  selector: "app-faq-support-page",
  templateUrl: "./faq-support-page.component.html",
  styleUrls: ["./faq-support-page.component.scss"],
})
export class FaqSupportPageComponent implements OnInit, OnDestroy {
  readonly themeStorageKey = "selectedTheme";
  selectedThemeMode: "light" | "dark" = "light";
  private themeSubscription?: Subscription;
  searchQuery: string = "";
  activeCategory: string = "General";
  isScrolled = false;
  isMobileView = false;
  
  categories: FAQCategory[] = [
    {
      name: "General",
      faqs: [
        {
          question: "What is Flowchest?",
          answer: "Flowchest is the execution layer for physical knowledge — turning know-how into step-by-step systems people can actually complete. Each guide combines ordered steps, required items, and safety context so someone can finish a real project, not just watch a video.",
          isOpen: true,
        },
        {
          question: "How is this different from tutorials or video platforms?",
          answer: "Tutorials are designed to be watched. Flowchest is designed to be used while doing. A flow is a structured process with a defined end state, not a long-form video you have to pause and rewind.",
          isOpen: false,
        },
        {
          question: "Is this just for DIY?",
          answer: "DIY is the starting point. Flowchest expands into repair, trades, education, product setup, and workplace training — anywhere physical know-how needs to be followed step by step.",
          isOpen: false,
        },
        {
          question: "Who is Flowchest for?",
          answer: "Makers and learners who want to complete a project, creators who want to publish and sell practical guides, and brands or retailers who need product-led instructions their customers can actually follow.",
          isOpen: false,
        },
        {
          question: "Do I need an account to view guides?",
          answer: "You can explore public guides on the Standard (free viewer) plan. Creating, publishing, or buying paid guides requires a signed-in account.",
          isOpen: false,
        },
        {
          question: "Can I monetize my content immediately?",
          answer: "Yes. After you join the Creator Network or a business plan, you can publish paid guides and bundles and earn through direct sales. Brands can also use Flowchest for product-led content and partnerships.",
          isOpen: false,
        },
        {
          question: "How do I follow a guide?",
          answer: "Open a public or purchased guide and work through the ordered steps while you do the project. Check the items list first, then complete each step in sequence. Video and photos support the step — they are not the whole experience. You can like, comment, and save progress as you go.",
          isOpen: false,
        },
        {
          question: "How do I buy a paid guide or bundle?",
          answer: "Open the guide or bundle, then checkout with PayPal. If the creator shared a discount code, enter it on the checkout page before you pay. After purchase, the full steps unlock on your account. You do not need a Creator or business plan just to buy and follow content.",
          isOpen: false,
        },
        {
          question: "What is the items list?",
          answer: "Every guide should list the tools, parts, and materials you need before step one. Gather those items first so you are not stopping mid-build. If something is missing, check the guide FAQs or comment to ask the creator.",
          isOpen: false,
        },
        {
          question: "Can I comment, like, or follow a creator?",
          answer: "Yes. On a guide you can like it, leave comments, and follow the creator to see more of their work. Keep comments practical and respectful — Community Guidelines apply.",
          isOpen: false,
        },
        {
          question: "How do I report inappropriate content?",
          answer: "Use Report on the guide or bundle if it is dangerous, abusive, stolen, or spam. Flowchest may remove content, warn the account, or suspend it. You can also send feedback from Contact Support.",
          isOpen: false,
        },
      ],
    },
    {
      name: "Plans & Billing",
      faqs: [
        {
          question: "What plans does Flowchest offer?",
          answer: "There are two individual paths and three business paths. Individuals can use Standard (free viewer) or Creator (commission-based publishing). Businesses can choose Starter, Growth, or Enterprise. Full current limits and prices are always on the Pricing page.",
          isOpen: false,
        },
        {
          question: "What is the Standard Plan?",
          answer: "Standard is free viewer access. You can browse and follow public guides with unlimited exploration. It does not include creating or publishing your own flows.",
          isOpen: false,
        },
        {
          question: "What is the Creator Plan?",
          answer: "Creator is for people who want to build, publish, and sell step-by-step guides. There is no monthly subscription fee. Hosting and reach are included, and Flowchest takes a small commission on each sale. New Creator flows and bundles are published as paid content. Creator does not include a monthly AI coin allowance — you can buy coin top-ups if you want Smart Create AI.",
          isOpen: false,
        },
        {
          question: "What is included in the Starter plan?",
          answer: "Starter is a product-led business plan (currently £99/month, or 20% less when billed annually). It is aimed at early-stage brands and pilots. Typical included limits are up to 3 published guides per month (10 steps each), up to 2,000 monthly views, 80 AI coins per month, and pay-as-you-go overage if you go beyond views. Check Pricing for the latest numbers.",
          isOpen: false,
        },
        {
          question: "What is included in the Growth plan?",
          answer: "Growth is our most popular business plan (currently £399/month, or 20% less when billed annually). It is aimed at established DTC brands and retailers. Typical included limits are up to 20 published guides per month (12–15 steps), up to 15,000 monthly views, 350 AI coins per month, view overage, and advanced access controls (such as IP, geo, device, and company-email restrictions). Check Pricing for the latest numbers.",
          isOpen: false,
        },
        {
          question: "What is the Enterprise plan?",
          answer: "Enterprise is a custom plan for large retailers and manufacturers. It includes unlimited guides and steps, custom SLAs and support, an advanced analytics dashboard, and a custom AI coin allowance with optional top-ups. Contact Sales to discuss pricing and setup.",
          isOpen: false,
        },
        {
          question: "Should I pick Creator or a business plan?",
          answer: "Choose Creator if you are an individual making and selling your own guides and prefer no monthly fee (commission on sales instead). Choose Starter or Growth if you are a brand that needs monthly guide and view allowances, included AI coins, and product-led infrastructure. Growth and Enterprise also unlock advanced access controls.",
          isOpen: false,
        },
        {
          question: "Can I upgrade or change my plan later?",
          answer: "Yes. You can move from Standard to Creator, or to Starter, Growth, or Enterprise as your needs grow. If a feature is locked (for example Smart Create coins or advanced access), you will be pointed to Pricing to upgrade or buy coins.",
          isOpen: false,
        },
        {
          question: "What happens if I exceed my guide or view limits?",
          answer: "On Starter and Growth, extra views are billed as overage (currently £0.02 per extra view). If you need more published guides than your plan includes, you can buy prepaid guide top-up slots instead of relying on pay-as-you-go. Guide slots never expire and are used only when you publish past your included allowance.",
          isOpen: false,
        },
        {
          question: "What optional add-ons are available?",
          answer: "Business customers can add Content Concierge (we build videos for you, from £300–£750 per guide), an Annual ESG Impact Report (£1,500 per brand), and on-site QR/packaging consulting (£2,000). These are optional and separate from your plan.",
          isOpen: false,
        },
        {
          question: "What are AI coins?",
          answer: "AI coins are credits that power Smart Create AI features — turning a full tutorial video into a complete flow, or drafting written step content from your step clips. They are not used for watching guides or for Manual Create.",
          isOpen: false,
        },
        {
          question: "How do I use AI coins?",
          answer: "Open Create Guide and choose Smart Create. Before AI runs, you will see a coin quote for that job. Plan coins are used first, then any top-up coins you have purchased. If the quote is higher than your balance, buy a top-up pack and try again.",
          isOpen: false,
        },
        {
          question: "How many coins does Smart Create cost?",
          answer: "It depends on the mode. A full-video AI import typically costs 8 coins for videos up to 15 minutes, or 12 coins for 15–20 minutes. Pieces AI (AI-written text for your step clips) typically costs 5 coins as a base plus 1 coin per step video. The exact cost is always shown before you confirm.",
          isOpen: false,
        },
        {
          question: "Which plans include AI coins each month?",
          answer: "Starter includes 80 AI coins per month and Growth includes 350. Enterprise can include a custom monthly allowance. Standard and Creator do not include monthly AI coins. Creator and Standard users can still buy top-up packs to use Smart Create.",
          isOpen: false,
        },
        {
          question: "Do AI coins expire?",
          answer: "Top-up coins never expire and stack on top of your plan balance. Monthly plan coins refresh with your billing period — unused plan coins do not roll forward as extra allowance beyond that period. Always check your balance on the Pricing page (AI Coins section) or in usage views.",
          isOpen: false,
        },
        {
          question: "What's the difference between plan coins and top-up coins?",
          answer: "Plan coins are the monthly allowance that comes with Starter, Growth, or Enterprise. Top-up coins are one-time purchases you buy when you need more. Your balance is shown as plan + top-up = total. Flowchest uses plan coins first, then top-up coins.",
          isOpen: false,
        },
        {
          question: "How do I buy more AI coins?",
          answer: "Go to Pricing and open the Purchase AI Coins section, or follow the Purchase coins link from Smart Create. Current packs are Top-up Try (20 coins, £4.99), Top-up Creator (70 coins, £14.99), and Top-up Studio (200 coins, £39.99). Packs are one-time purchases paid with PayPal.",
          isOpen: false,
        },
        {
          question: "Do I get any coins when I start?",
          answer: "New eligible accounts may receive a small welcome bonus (currently 5 top-up coins) so you can try Smart Create. After that, use your plan allowance or buy a top-up pack.",
          isOpen: false,
        },
        {
          question: "What if I don't have enough coins?",
          answer: "Smart Create will show how many coins you need and how many you are short. You can buy a top-up pack and return to the same create screen, or write the steps yourself with Manual Create / manual captions, which does not spend AI coins.",
          isOpen: false,
        },
        {
          question: "How does the annual plan discount work?",
          answer: "Starter and Growth save 20% when you choose annual billing. You still see a monthly equivalent, but you are billed for the year up front. Toggle Monthly / Annual on the Pricing page to compare.",
          isOpen: false,
        },
        {
          question: "Can I offer a discount on my own guides?",
          answer: "Yes. Creators and business-plan users can generate percentage discount codes for flows, bundles, or both. Open User Settings → Discount Code Settings and choose Generate Discount Code. Set the code (max 12 characters), where it applies, the percentage, and a start/end date.",
          isOpen: false,
        },
        {
          question: "How do buyers use a discount code?",
          answer: "At checkout, enter the code in the Discount Code field and click Apply. If the code is valid for that content type and still in date, the discounted price is shown before PayPal payment. Invalid or expired codes are rejected.",
          isOpen: false,
        },
        {
          question: "What can a discount code apply to?",
          answer: "When you create a code you choose Guides, Guide Packs, or Both. A flow-only code will not reduce a bundle checkout, and vice versa. Codes do not apply to donations, AI coin packs, or plan subscriptions.",
          isOpen: false,
        },
        {
          question: "How many active discount codes can I have?",
          answer: "It depends on your plan. Creator typically allows 3 active codes, Starter 10, and Growth 25. Standard (viewer) cannot create codes. If you hit the limit, let an old code expire or contact support.",
          isOpen: false,
        },
        {
          question: "Why didn't my discount code work?",
          answer: "Common reasons: the code is mistyped, it has expired or not started yet, it does not apply to that content type (flow vs bundle), it was already used where usage is limited, or it was created by a different seller. Codes only work on that creator’s eligible paid content.",
          isOpen: false,
        },
        {
          question: "Can I see how many times a code was used?",
          answer: "Yes. Your Discount Codes list shows each code, where it applies, the percentage, date range, status, and usage count so you can track campaigns.",
          isOpen: false,
        },
        {
          question: "Is Flowchest a marketplace?",
          answer: "No. It's infrastructure that creators and brands build on. You publish executable guides; buyers pay for access to complete them. Flowchest is not a generic content storefront.",
          isOpen: false,
        },
        {
          question: "How do I pay for plans, coins, or guides?",
          answer: "Payments are processed with PayPal at checkout — whether you are subscribing to Starter or Growth, buying an AI coin or guide top-up pack, or purchasing a creator’s paid flow or bundle.",
          isOpen: false,
        },
        {
          question: "Where can I see my usage and coin balance?",
          answer: "Your plan usage (guides, views, AI coins) is available from subscription/usage views in your account. Coin balance is also shown on the Pricing page in the AI Coins section: plan coins + top-up coins = total available.",
          isOpen: false,
        },
        {
          question: "How do I manage or cancel a subscription?",
          answer: "Open Subscriptions from your account to see your current plan, billing period, and usage. To change plan, go to Pricing. For billing problems or cancellation help, use Contact Support.",
          isOpen: false,
        },
      ],
    },
    {
      name: "Creating & Sharing",
      faqs: [
        {
          question: "What is the Creator community (Creator Network)?",
          answer: "The Creator Network is Flowchest's community of people who turn real-world know-how into executable guides. Joining activates the Creator plan on your account so you can create, publish, and sell step-by-step flows. It is a network of practitioners — not a social feed — built around helping others finish projects.",
          isOpen: false,
        },
        {
          question: "How do I join the Creator Network?",
          answer: "Go to the Pricing page and choose Join Creator Network. Confirm to activate the Creator plan. There is no monthly subscription fee. You can then start creating from Create Guide.",
          isOpen: false,
        },
        {
          question: "How do creators make money?",
          answer: "By helping people successfully complete real projects. You can sell individual guides and bundles (Flowchest takes a small commission per sale to support hosting and reach) and work with brands through product-led partnerships.",
          isOpen: false,
        },
        {
          question: "Do creators need to make lots of video?",
          answer: "No. Video supports execution — it doesn't lead it. A strong guide can use short step clips, one tutorial video, photos, or clear written steps. What matters is a defined outcome, ordered steps, required items, and safety notes.",
          isOpen: false,
        },
        {
          question: "Is Creator content free or paid?",
          answer: "On the Creator plan, new flows and bundles are published as paid content. Buyers pay you for access; Flowchest takes a small commission. Public viewing of other people's free/public guides remains available on Standard.",
          isOpen: false,
        },
        {
          question: "What are the community guidelines?",
          answer: "Flowchest exists to help people finish what they start, which only works if the community is respectful, honest, and practical. Share clear, accurate guidance from real experience. Do not post dangerous instructions, hate or harassment, false qualifications, stolen content, or spam. Flag safety-critical steps clearly. See Community Guidelines for the full policy.",
          isOpen: false,
        },
        {
          question: "Can brands join the same community?",
          answer: "Yes. Brands and retailers publish product-led guides on Starter, Growth, or Enterprise, while independent creators publish on the Creator plan. Learners follow those guides to complete real work. One platform connects all three.",
          isOpen: false,
        },
        {
          question: "How do I create a guide?",
          answer: "Sign in with a Creator or business plan, then choose Create Guide. Pick Smart Create for a guided upload (full video, step clips, or AI-written steps) or Manual Create if you want full control over cover, items, steps, and FAQs tab by tab.",
          isOpen: false,
        },
        {
          question: "What's the difference between Smart Create and Manual Create?",
          answer: "Smart Create is one guided flow for every upload style: one full tutorial video that AI turns into a whole flow, or separate step clips where you write captions yourself or let AI draft the text. Items, FAQs, and pricing are reviewed on the same screen. Manual Create walks you through each section yourself and does not spend AI coins.",
          isOpen: false,
        },
        {
          question: "How can I create the best content?",
          answer: "Start with a clear end state — what “done” looks like — then break the work into short, ordered steps someone can follow while doing. List required items up front, keep each step to one action, add a clip or photo only where it helps execution, and write FAQs for the questions people actually get stuck on. Flag hazards. Accuracy beats production value.",
          isOpen: false,
        },
        {
          question: "What should every good Flowchest guide include?",
          answer: "A defined outcome, a complete items list, numbered steps with enough detail to act, safety notes on critical steps, and FAQs for common mistakes. Optional extras that help: supporting documents, links, pricing if the guide is paid, and a bundle if several guides belong together.",
          isOpen: false,
        },
        {
          question: "How should I structure steps?",
          answer: "One step = one action. Keep language specific (“tighten the two bolts on the left bracket”) rather than vague (“assemble the frame”). Show the result of the step so the learner can check they are still on track. Stay within your plan’s step limit (for example 10 on Starter, 12–15 on Growth, up to 20 on Creator).",
          isOpen: false,
        },
        {
          question: "Should I upload one long video or separate step clips?",
          answer: "Use one full tutorial if you already have a complete walkthrough — Smart Create can split it into a flow (this uses AI coins). Use separate step clips when you want tighter control and shorter, task-sized videos. You can also write steps yourself and skip AI entirely.",
          isOpen: false,
        },
        {
          question: "Can AI write my step content for me?",
          answer: "Yes. In Smart Create, add your step clips (and an optional reference document), then Generate with AI. Always review and edit the draft — you are responsible for accuracy and safety. Rephrase tools can also help polish titles and descriptions.",
          isOpen: false,
        },
        {
          question: "How do I add items, FAQs, and pricing?",
          answer: "During create or review, add every tool, part, and material the learner needs, then add grouped FAQs for setup, safety, and troubleshooting. If the guide is paid, set a price before publish. You can also group related guides into a bundle.",
          isOpen: false,
        },
        {
          question: "Any safety tips for creators?",
          answer: "Call out safety-critical steps clearly, never encourage unsafe shortcuts, and respect local laws. Flowchest may remove content that poses a safety risk. If a step could injure someone when done wrong, say so in the step and in FAQs.",
          isOpen: false,
        },
        {
          question: "What is a bundle?",
          answer: "A bundle groups related guides so learners can buy or follow a set together — for example a full product line or a multi-part project. Creators add existing flows into a bundle, set a bundle price if it is paid, and can share a bundle QR code.",
          isOpen: false,
        },
        {
          question: "How do I share a guide or bundle?",
          answer: "Open Share on the guide or bundle. Copy the page link, share to Facebook, X, WhatsApp, LinkedIn, or email, generate a QR code to download, or copy an embed snippet for your website.",
          isOpen: false,
        },
        {
          question: "How do QR codes work?",
          answer: "Each guide, bundle, and profile can have a QR code that opens that page when scanned. Download the code and print it on packaging, a product card, or a poster so people can start the flow in the real world. This is the same idea as on-site QR / packaging consulting for brands.",
          isOpen: false,
        },
        {
          question: "Can I embed a guide on my website?",
          answer: "Yes. Open Share → Embed and copy the embed code for a guide or a bundle. Paste it into your site or product page so customers can follow the steps without leaving your brand experience. Paid or restricted guides still enforce purchase and access rules inside the embed.",
          isOpen: false,
        },
        {
          question: "Who can see my guide?",
          answer: "You choose when you publish. Public guides are discoverable. Paid guides need a purchase. Private keeps it to you. Restricted lets you share by link, selected users, or (on Growth and Enterprise) advanced rules such as IP/network, location, device, or company email domain.",
          isOpen: false,
        },
        {
          question: "What is advanced access control?",
          answer: "Growth and Enterprise can require extra checks on top of who is invited: allowed IP ranges or networks, a geo radius around a site, specific device IDs, and/or company email domains (for example @yourbrand.com). All enabled methods must pass. Starter and Creator do not include these advanced controls.",
          isOpen: false,
        },
        {
          question: "What if I cannot open a restricted guide?",
          answer: "You may need to sign in, purchase it, join the allowed network or location, use an approved device, or use a company email. If the owner allows it, you can submit an access request. They review requests from Dashboard → Access Requests.",
          isOpen: false,
        },
        {
          question: "How do access requests work?",
          answer: "When a restricted guide blocks you, you can request access. The owner sees pending requests in their dashboard, then approves or declines. Once approved, you can open the guide on that account (advanced rules like network or geo may still apply).",
          isOpen: false,
        },
        {
          question: "What are consultations?",
          answer: "Creators can offer paid 1:1 sessions tied to a guide — for example a live walkthrough on Google Meet, Zoom, or Microsoft Teams. You set availability, duration, price per slot, and timezone in User Settings → Consultation Settings, then learners book from the guide.",
          isOpen: false,
        },
        {
          question: "How do I book a consultation?",
          answer: "On a guide that offers consultations, choose Book a Consultation. Pick a duration and price, then a date and time in the host’s timezone, and complete checkout. You will see the booking in your dashboard under Initiated Consultation.",
          isOpen: false,
        },
        {
          question: "Where do I manage consultation bookings?",
          answer: "Creators review incoming bookings under Dashboard → Received Consultation, including reschedule. Learners see what they booked under Initiated Consultation. Connect a calendar in consultation settings if you want slots to stay in sync.",
          isOpen: false,
        },
        {
          question: "Why are no time slots available?",
          answer: "The host may not have enabled any durations, may be fully booked that week, or may have turned consultations off. Try another week, or contact the creator. Hosts should enable at least one duration and save Consultation Settings.",
          isOpen: false,
        },
        {
          question: "Can I collaborate on a guide with someone else?",
          answer: "Yes. Turn on collaboration when creating a flow and invite a co-author. Configure how others can collaborate with you in User Settings → Collaboration Settings (type, bio, visibility). Co-authors are notified and can accept from Collaboration Requests.",
          isOpen: false,
        },
        {
          question: "How do collaboration requests work?",
          answer: "When someone invites you as a co-author, the request appears in Collaboration Requests. Accept or decline there. Until it is accepted, the guide may show as collaboration pending. Earnings share, if any, follows the collaboration value set on the flow.",
          isOpen: false,
        },
        {
          question: "Can I transfer ownership of a guide?",
          answer: "Yes. You can send an ownership transfer. Outgoing transfers appear under Dashboard → Outgoing Ownership; the other person accepts or declines under Incoming Ownership. Do not transfer unless you intend to give up control of that content.",
          isOpen: false,
        },
      ],
    },
    {
      name: "Account",
      faqs: [
        {
          question: "How do I create an account?",
          answer: "Choose Get Started or Sign Up and complete registration. You will need to activate the account from the email we send. After that you can explore public guides on Standard, or join Creator / a business plan to publish.",
          isOpen: false,
        },
        {
          question: "I forgot my password. How do I reset it?",
          answer: "Use Forgot Password on the login screen or User Settings → Forgot Password. We send a reset link to your email. You can also change a known password under Change Password. Passkeys are a separate sign-in method and do not replace this.",
          isOpen: false,
        },
        {
          question: "How do I change light or dark theme?",
          answer: "Use the theme toggle in the header (sun/moon). Your choice is saved to your profile when you are signed in so it follows you on this account.",
          isOpen: false,
        },
        {
          question: "How do notifications work?",
          answer: "Open User Settings → Notification Settings to choose what you are notified about (for example comments, follows, purchases, or collaboration). You can turn types on or off there.",
          isOpen: false,
        },
        {
          question: "Can people donate to me?",
          answer: "Yes, if you enable donations in User Settings → Donation Settings. Supporters can send a donation from your profile or content. You can review received amounts under Dashboard → Donated Funds.",
          isOpen: false,
        },
        {
          question: "Where do I see analytics and reviews?",
          answer: "Open Dashboard for an overview, Analytics for content performance (views, engagement), and Flow Reviews for feedback on your guides. This helps you see which steps people complete and where they get stuck.",
          isOpen: false,
        },
        {
          question: "How do I delete my account?",
          answer: "Go to User Settings → Delete Account. This is permanent. Export or transfer any guides you need first, cancel paid subscriptions if you have them, and remove Passkeys you no longer need.",
          isOpen: false,
        },
        {
          question: "What is a Passkey?",
          answer: "A Passkey lets you sign in to Flowchest with Windows Hello, Face ID, Touch ID, a fingerprint, or your phone — without typing your password. It is tied to your device and this site, and is a faster, phishing-resistant alternative to a password. Your password still works as a fallback.",
          isOpen: false,
        },
        {
          question: "How do I create a Passkey?",
          answer: "After you sign in, you may see a prompt to Create Passkey. You can also go to User Settings → Passkeys. Optionally add a device name (for example Work laptop or iPhone), then choose Create passkey, Add on this device, or Add with phone. Approve the browser prompt with biometrics or a PIN.",
          isOpen: false,
        },
        {
          question: "How do I sign in with a Passkey?",
          answer: "On the login screen, enter your email first. If your account has a Passkey and this browser supports them, Flowchest offers Sign in with Passkey. Confirm with Windows Hello, Face ID, Touch ID, or your phone. You can switch to Use password instead at any time.",
          isOpen: false,
        },
        {
          question: "Can I still use my password if I have a Passkey?",
          answer: "Yes. Passkeys do not replace your password. Choose Use password instead on the login screen if biometrics fail, you are on an unsupported device, or you prefer to type your password.",
          isOpen: false,
        },
        {
          question: "How do I sign in on another computer?",
          answer: "Choose Sign in with Passkey, then use phone / QR. Scan the code with the phone that already has your Passkey — use Camera, Google Password Manager, or iPhone Passkeys, not a generic QR search app. Keep the computer dialog open; sign-in finishes on the computer, not on the phone.",
          isOpen: false,
        },
        {
          question: "Why didn't the Passkey QR code work?",
          answer: "The code is a FIDO Passkey challenge, not a website link. Do not scan it with a generic QR or URL-search app. Use Camera, Google Password Manager, or iPhone Passkeys on the phone that already has the Passkey. Keep Bluetooth and internet on for both devices, and keep the computer dialog open until it completes. Phone QR is available on https://app.flowchest.com; it does not work on localhost.",
          isOpen: false,
        },
        {
          question: "Which browsers and devices support Passkeys?",
          answer: "Use a current Chrome, Edge, Safari, or Firefox on a device with biometrics (Windows Hello, Face ID, Touch ID) or a security key. The site must be HTTPS (app.flowchest.com or an allowed staging URL). If Passkeys are not supported, you will see a message and can keep using your password.",
          isOpen: false,
        },
        {
          question: "How do I rename or remove a Passkey?",
          answer: "Go to User Settings → Passkeys. Each registered Passkey shows its name, whether it is this device or a phone/security key, when it was added, and when it was last used. Rename it so you can recognize it later, or Remove it if you no longer want to sign in with that device. Removing a Passkey does not delete your account or password.",
          isOpen: false,
        },
        {
          question: "Why am I asked to create a Passkey after signing in with a password?",
          answer: "Flowchest may offer Make sign-in easier after a password login so you can add a Passkey on this device. You can Create Passkey or choose Not now. If you skip, the prompt can appear again later (including on a new computer). After a few skips it stops asking on that browser.",
          isOpen: false,
        },
        {
          question: "Can I add a Passkey on more than one device?",
          answer: "Yes. Add one on this PC with Add on this device, and another on your phone with Add with phone. On a new computer you can also create a Passkey for that machine. Use a friendly name for each so you know which to keep or remove.",
          isOpen: false,
        },
        {
          question: "Do Passkeys created on staging work on app.flowchest.com?",
          answer: "No. A Passkey is bound to that exact site. Staging (Azure Static Web Apps) Passkeys only work on that staging URL. Production Passkeys only work on https://app.flowchest.com. Create a separate Passkey on the site you actually sign in to.",
          isOpen: false,
        },
      ],
    },
    {
      name: "Safety & Privacy",
      faqs: [
        {
          question: "Is my data secure and private?",
          answer: "Yes. We take data security and privacy seriously. Account and payment data is handled with encryption and stored securely. See our Privacy Policy for details on what we collect and why.",
          isOpen: false,
        },
        {
          question: "Who can see my guides?",
          answer: "Public guides can be viewed by anyone exploring Flowchest. Paid guides are only fully accessible after purchase (or to you as the owner). You can also make a guide private or restricted. Growth and Enterprise can add advanced restrictions such as IP, geography, device, or company email. See Creating & Sharing for access control details.",
          isOpen: false,
        },
      ],
    }
  ];

  filteredFAQs: FAQCategory[] = [];

  constructor(
    private router: Router,
    private dataService: DataService,
    private flowServiceService: FlowServiceService,
    private eventEmitterService: EventEmitterService,
    private userserviceService: UserserviceService,
    private themePreferenceService: ThemePreferenceService
  ) {}

  ngOnInit(): void {
    this.themeSubscription = this.themePreferenceService.themeChanges$.subscribe((theme) => {
      this.selectedThemeMode = theme;
    });
    this.initializeThemeMode();
    this.flowServiceService.setScrollTop();
    this.eventEmitterService.callEvent();
    this.eventEmitterService.setHomePageIconColorEvent("home");
    this.filteredFAQs = this.categories;
    this.checkMobileView();
    this.setupScrollTracking();
    
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.checkMobileView.bind(this));
    }
  }

  checkMobileView(): void {
    if (typeof window !== 'undefined') {
      this.isMobileView = window.innerWidth <= 768;
    }
  }

  toggleSidebar(): void {
    const event = new CustomEvent('toggleSidebar');
    window.dispatchEvent(event);
  }

  get isDarkThemeActive(): boolean {
    return this.selectedThemeMode === "dark";
  }

  toggleThemeMode(): void {
    const nextTheme = this.themePreferenceService.toggleTheme();
    this.persistThemeMode(nextTheme);
  }

  private persistThemeMode(theme: "light" | "dark"): void {
    const userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    if (userData?.emailAddress) {
      this.userserviceService
        .UpdateThemeSettingsForCustomerProfileDetails({
          emailAddress: userData.emailAddress,
          selectedTheme: theme,
        })
        .subscribe({
          next: () => {
            userData.selectedTheme = theme;
            localStorage.setItem("LoggedInUser", JSON.stringify(userData));
          },
          error: () => {},
        });
    }
  }

  private initializeThemeMode(): void {
    const theme = this.themePreferenceService.initializeFromStorage();
    this.selectedThemeMode = theme;
  }

  ngOnDestroy(): void {
    this.themeSubscription?.unsubscribe();
  }

  homePage(): void {
    this.router.navigate(["/"]);
  }

  setupScrollTracking(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', () => {
        this.isScrolled = window.scrollY > 50;
      }, { passive: true });
    }
  }

  navigateToSignUp(): void {
    this.router.navigate(['/registration']);
  }

  toggleFAQ(faq: FAQItem): void {
    if (!faq) {
      return;
    }
    faq.isOpen = !faq.isOpen;
  }

  setActiveCategory(categoryName: string): void {
    this.activeCategory = categoryName;
    this.searchQuery = this.searchQuery || "";
    this.filterFAQs();
  }

  filterFAQs(): void {
    if (!this.searchQuery.trim()) {
      this.filteredFAQs = this.categories;
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredFAQs = this.categories.map((category) => {
      const filtered = category.faqs.filter(
        (faq) =>
          !!faq?.question &&
          !!faq?.answer &&
          (faq.question.toLowerCase().includes(query) ||
            faq.answer.toLowerCase().includes(query))
      );
      return { ...category, faqs: filtered };
    }).filter((category) => category.faqs.length > 0);
  }

  onSearchChange(): void {
    this.filterFAQs();
  }

  trackFaq(_index: number, faq: FAQItem): string {
    return faq?.question || String(_index);
  }

  getCurrentCategoryFAQs(): FAQItem[] {
    const isValidFaq = (faq: FAQItem) => !!faq?.question && !!faq?.answer;
    if (this.searchQuery.trim()) {
      return this.filteredFAQs.reduce(
        (all: FAQItem[], category) => all.concat(category.faqs.filter(isValidFaq)),
        []
      );
    }
    const category = this.filteredFAQs.find(
      (cat) => cat.name === this.activeCategory
    );
    return category ? category.faqs.filter(isValidFaq) : [];
  }

  contactSupport(): void {
    // Navigate to contact/support page or open support dialog
    this.router.navigate(["/send-feedback"]);
  }
}

